import { MessageHelper, NotificationType } from "commonui";
import { GLGlobal, LanguageDateFormat, LanguageTimeFormatWithoutSecond, LanguageTimeFormat } from "@/commonui/utils";
import moment from "moment";
import * as FileSaver from "file-saver";
import { CustomStatusCodes } from "./enum";

export class InvitationHelper {
	static sendEmailSuccess(
		description: string = "Invitation sent successfully" // GLGlobal.intl.formatMessage({ id: GSAdminLocale.InvitationSendSuccess })
	) {
		MessageHelper.Message(NotificationType.Success, description);
	}

	static sendEmailFail(
		description: string = "Invitation fail" // GLGlobal.intl.formatMessage({ id: GSAdminLocale.InvitationRemoveFail })
	) {
		MessageHelper.Message(NotificationType.Failed, description);
	}
}

export class DateHelper {
	// GLGlobal.intl.locale
	static enLocale = "en";
	static formatUTCDateString(date: string | moment.Moment | Date) {
		if (!date) {
			return "";
		}
		return moment.utc(date).format(LanguageDateFormat[DateHelper.enLocale]);
	}

	static toUTCDateString(date: string) {
		if (!date) {
			return date;
		}
		return moment.utc(moment(date).format(`YYYY-MM-DD`)).toISOString();
	}

	static formatDate2Local(date: string | moment.Moment | Date) {
		if (!date) {
			return "";
		}
		const localTime = moment.utc(date).toDate();
		return moment(localTime).format(LanguageDateFormat[DateHelper.enLocale]);
	}

	static formatDate2LocalDate(date: string | moment.Moment | Date) {
		if (!date) {
			return null;
		}
		const localTime = moment.utc(date).toDate();
		return moment(localTime).toDate();
	}

	// format is for dates which are not ISO formatted. So define the format of date by using symbols in moment.
	static formatDateTime2Local(
		date: string | moment.Moment,
		toUtc: boolean = false,
		format?: string,
		removeSecond: boolean = false
	) {
		const localTime = toUtc ? moment.utc(date, format).toDate() : moment(date, format).toDate();
		const timeFormat = removeSecond
			? LanguageTimeFormatWithoutSecond[DateHelper.enLocale]
			: LanguageTimeFormat[DateHelper.enLocale];
		return moment(localTime).format(`${LanguageDateFormat[DateHelper.enLocale]} ${timeFormat}`);
	}

	static formatDateTime2LocalWithMilliSeconds(date: string | moment.Moment, toUtc: boolean = false) {
		const localTime = toUtc ? moment.utc(date).toDate() : moment(date).toDate();
		return moment(localTime).format(
			`${LanguageDateFormat[DateHelper.enLocale]} ${LanguageTimeFormat[DateHelper.enLocale]}.SSS`
		);
	}

	static formatDate(date: string, format) {
		const localTime = moment.utc(date).toDate();
		return moment(localTime).format(format);
	}

	static formatDate2ISO(date: string | moment.Moment) {
		const d = moment(date);
		d.set("hour", 0);
		d.set("minute", 0);
		d.set("second", 0);
		d.set("millisecond", 0);
		return d.toISOString();
	}

	static toLocalMoment(date: string | moment.Moment, format?: moment.MomentFormatSpecification, strict?: boolean) {
		const localTime = moment.utc(date).toDate();
		return moment(localTime, format, strict);
	}

	static toLocalDate(date: string | moment.Moment | Date) {
		if (!date) {
			return "";
		}

		const localTime = moment(date).toDate();
		return moment(localTime).format(LanguageDateFormat[DateHelper.enLocale]);
	}

	static formatDateAsIs(date: string, format?: string) {
		if (!date) {
			return "";
		}

		if (format) {
			return moment(new Date(date)).format(format);
		} else {
			return moment(new Date(date)).format(LanguageDateFormat[DateHelper.enLocale]);
		}
	}
}

export class BlobHelper {
	static saveFile(response, fileName?): boolean {
		if (
			!response ||
			((!response.body || !response.body.size) && (!response.data || !response.data.size)) ||
			(!fileName && !response.header["content-disposition"])
		) {
			return false;
		}
		const file = !fileName && response.header["content-disposition"].split(";")[1].trim().split("=");
		const name = fileName ? fileName : file.length > 1 ? file[1].replace(/["]/g, "") : "file";
		const blob = new Blob([response.data.size ? response.data : response.body]);
		FileSaver.saveAs(blob, name);
		return true;
	}
}

export class ResponseHelper {
	static isForbiddenResponse(reason: any) {
		return !!(
			reason &&
			reason.body &&
			reason.body.statusCode &&
			reason.body.statusCode === CustomStatusCodes.Forbidden
		);
	}
}
